import { createPackagePickerService } from '../../services';
import _ from 'lodash';
import { getSelectedComponentAppData as getSelectedComponentAppDataUtil } from '../utils/handlerUtils';
import { createWindowPlacementHandlers } from './windowPlacementHandlers';
import { createProductHandlers } from './productHandlers';
import { createComponentHandlers } from './componentHandlers';
import { createSettingsHandlers } from './settingsHandlers';

import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type { DocumentServicesObject } from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import type {
  TpaStyleService,
  TpaMediaDialogService,
  TpaSettingsPanelService,
  AppRefreshService,
} from '../../services';
import { createStyleHandlers } from './styleHandlers';
import { createInstallationHandlers } from './installationHandlers';

interface AppEngagedMessageData {
  premiumIntent?: string;
}

export interface EditorTPAHandlers {
  setStyleParam: TPAHandler;
  setExternalId: TPAHandler;
  closeWindow: TPAHandler;
  getDashboardAppUrl: TPAHandler;
  getExternalId: TPAHandler;
  isApplicationInstalled: TPAHandler;
  settingsOpenModal: TPAHandler;
  setGlobalStyleParam: TPAHandler;
  getWindowPlacement: TPAHandler;
  getPermissions: TPAHandler;
  appEngaged: TPAHandler<AppEngagedMessageData>;
  isComponentInstalled: TPAHandler;
  postMessage: TPAHandler;
  appIsAlive: TPAHandler;
  refreshAppsOnDashboardClose: TPAHandler;
  stylesReady: TPAHandler;
  editorIsFullWidth: TPAHandler;
  editorIsApplicationInstalled: TPAHandler;
}

export type TpaHandlerType = keyof EditorTPAHandlers;

interface TpaHandlersServicesDependencies {
  tpaSettingsPanelService: TpaSettingsPanelService;
  tpaMediaDialogService: TpaMediaDialogService;
  tpaStyleService: TpaStyleService;
}

export const createEditorTPAHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  appRefreshService: AppRefreshService,
  tpaHandlersServices: TpaHandlersServicesDependencies,
): EditorTPAHandlers => {
  const { tpaSettingsPanelService, tpaMediaDialogService, tpaStyleService } =
    tpaHandlersServices;

  const getSelectedComponentAppData = () =>
    getSelectedComponentAppDataUtil(platformApiMethods, documentServicesAPI);

  const packagePickerService = createPackagePickerService(
    platformApiMethods,
    documentServicesAPI,
  );

  const installationHandlers = createInstallationHandlers(
    platformApiMethods,
    documentServicesAPI,
    getSelectedComponentAppData,
  );

  const windowPlacementHandlers = createWindowPlacementHandlers(
    platformApiMethods,
    documentServicesAPI,
  );

  const productHandlers = createProductHandlers(
    platformApiMethods,
    documentServicesAPI,
    packagePickerService,
    getSelectedComponentAppData,
  );

  const componentHandlers = createComponentHandlers(
    platformApiMethods,
    documentServicesAPI,
    tpaMediaDialogService,
    getSelectedComponentAppData,
  );

  const settingsHandlers = createSettingsHandlers(
    platformApiMethods,
    documentServicesAPI,
    appRefreshService,
    tpaSettingsPanelService,
    getSelectedComponentAppData,
  );

  const styleHandlers = createStyleHandlers(
    platformApiMethods,
    documentServicesAPI,
    tpaStyleService,
  );

  const setExternalId = (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message data for setExternalId handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error(
        `Component with id ${msg.compId} was not found in the document`,
      );
    }

    documentServicesAPI.tpa.comp.setExternalId(
      compPointer,
      msg.data?.externalId,
      callback,
      msg.data?.preventRefresh,
    );
  };

  const getExternalId = (msg: TPAMessage, callback: TPAMessageCallback) => {
    if (!msg.compId) {
      throw new Error(
        '"compId" should be present in the message data for getExternalId handler',
      );
    }

    const compPointer = documentServicesAPI.components.get.byId(msg.compId);

    if (!compPointer) {
      throw new Error(
        `Component with id ${msg.compId} was not found in the document`,
      );
    }

    const externalId = documentServicesAPI.tpa.comp.getExternalId(compPointer);
    callback(externalId);
  };

  const noopForBIEvents = async (
    _msg: TPAMessage,
    _cb: TPAMessageCallback,
  ) => {};

  return {
    ...windowPlacementHandlers,
    ...installationHandlers,
    ...componentHandlers,
    ...settingsHandlers,
    ...productHandlers,
    ...styleHandlers,
    setExternalId,
    getExternalId,
    getPermissions: noopForBIEvents,
  };
};
